/*
 * WF Effects — original interaction layer for Wig100 (on top of Horizon).
 * Loaded as a module by sections/wf-effects.liquid and by every wf-* section,
 * so it runs once per page even if referenced several times.
 * Nothing in here edits Horizon files; it only reads Horizon markup and uses
 * Horizon's public cart events so the native cart drawer / bubble update.
 */

const WF = (window.WF = window.WF || {});
if (!WF.__loaded) {
  WF.__loaded = true;
  document.documentElement.classList.add('wf-js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isEditor = Boolean(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */
  const debounce = (fn, wait = 80) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));

  // Horizon 4.x scrolls inside .page-wrapper on desktop (html/body are overflow: hidden),
  // so scroll-driven effects must listen to the nearest scrolling ancestor.
  const scrollParent = (el) => {
    let n = el && el.parentElement;
    while (n && n !== document.body && n !== document.documentElement) {
      const oy = getComputedStyle(n).overflowY;
      if (oy === 'auto' || oy === 'scroll') return n;
      n = n.parentElement;
    }
    return window;
  };
  WF.scrollParent = scrollParent;

  const lockScroll = (on) => {
    const root = document.documentElement;
    root.classList.toggle('wf-lock', on);
    // Horizon's own lock attribute also freezes .page-wrapper
    if (on) root.setAttribute('scroll-lock', '');
    else root.removeAttribute('scroll-lock');
  };

  const readConfig = () => {
    const el = document.getElementById('wf-effects-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  };

  const money = (cents) => {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };
  WF.money = money;

  const SPARKLE_SVG =
    '<svg class="wf-sparkle" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M10 2l1.9 5.6L17.5 9.5l-5.6 1.9L10 17l-1.9-5.6L2.5 9.5l5.6-1.9z"/>' +
    '<path fill="currentColor" d="M18.5 13l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9z"/>' +
    '</svg>';

  /* ------------------------------------------------------------------ */
  /* Cart: add items and let Horizon's drawer / bubble react natively    */
  /* ------------------------------------------------------------------ */
  WF.addToCart = async function addToCart(items, sourceEl) {
    const lines = items.filter((i) => i && i.id).map((i) => ({ id: Number(i.id), quantity: Number(i.quantity) || 1 }));
    if (!lines.length) throw new Error('No variant selected');

    let EventsMod = null;
    try {
      EventsMod = await import('@shopify/events');
    } catch (e) {
      EventsMod = null;
    }
    const CartLinesUpdateEvent = EventsMod && EventsMod.CartLinesUpdateEvent;
    const deferred = CartLinesUpdateEvent && CartLinesUpdateEvent.createPromise ? CartLinesUpdateEvent.createPromise() : null;

    if (CartLinesUpdateEvent && deferred) {
      (sourceEl || document.body).dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
          promise: deferred.promise,
        })
      );
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset && el.dataset.sectionId)
      .filter(Boolean);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const body = { items: lines };
    if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

    let response;
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      response = await res.json();
      if (!res.ok || response.status) {
        throw new Error(response.description || response.message || 'Could not add to cart');
      }
    } catch (error) {
      const cart = await fetch(root + 'cart.js', { credentials: 'same-origin' }).then((r) => r.json()).catch(() => null);
      if (deferred && cart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'wf-effects', sourceId: 'wf-effects', itemCount: 0 },
        });
      } else if (deferred) {
        deferred.reject(error);
      }
      throw error;
    }

    const cart = await fetch(root + 'cart.js', { credentials: 'same-origin' }).then((r) => r.json());
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'wf-effects',
          sourceId: 'wf-effects',
          itemCount: lines.reduce((n, l) => n + l.quantity, 0),
          sections: response.sections,
          didError: false,
        },
      });
    } else {
      // Fallback when Horizon's event module is unavailable: refresh to show the cart.
      document.dispatchEvent(new CustomEvent('wf:cart-added', { detail: { cart } }));
    }
    return cart;
  };

  /* ------------------------------------------------------------------ */
  /* Scroll reveal                                                       */
  /* ------------------------------------------------------------------ */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-in');
                revealObserver.unobserve(entry.target);
              }
            }
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        )
      : null;

  WF.reveal = (root = document) => {
    root.querySelectorAll('[data-wf-reveal]:not(.is-in)').forEach((el) => {
      if (!revealObserver || reduceMotion.matches) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Header menu: highlight item, promo cards, page scrim                */
  /* ------------------------------------------------------------------ */
  const Menu = {
    config: { highlight: [], promos: [] },
    scrim: null,

    init() {
      const cfg = readConfig();
      this.config.highlight = (cfg.highlight || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean);
      this.config.promos = cfg.promos || [];
      this.config.scrim = cfg.scrim !== false;
      if (!document.documentElement.classList.contains('wf-menu-fx')) return;

      this.decorate();
      const header = document.querySelector('#header-component') || document.querySelector('header');
      if (!header) return;

      const redecorate = debounce(() => this.decorate(), 60);
      if (this.observer) this.observer.disconnect();
      this.observer = new MutationObserver((mutations) => {
        let structural = false;
        for (const m of mutations) {
          if (m.type === 'childList') structural = true;
        }
        if (structural) redecorate();
        this.syncScrim();
      });
      this.observer.observe(header, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-expanded'] });

      if (!this.boundLeave) {
        this.boundLeave = true;
        header.addEventListener('pointerleave', () => setTimeout(() => this.syncScrim(), 120));
        header.addEventListener('pointerover', () => this.syncScrim());
      }
    },

    titleOf(link) {
      const t = link.querySelector('.menu-list__link-title');
      return ((t ? t.textContent : link.textContent) || '').trim().toLowerCase();
    },

    decorate() {
      const links = document.querySelectorAll('header .menu-list__link');
      links.forEach((link) => {
        const title = this.titleOf(link);
        if (this.config.highlight.includes(title) && !link.classList.contains('wf-hl')) {
          link.classList.add('wf-hl');
          const label = link.querySelector('.menu-list__link-title') || link;
          if (!link.querySelector('.brand-sparkle, .wf-sparkle')) label.insertAdjacentHTML('beforeend', SPARKLE_SVG);
        }
      });

      for (const promo of this.config.promos) {
        if (!promo || !promo.menu) continue;
        const key = String(promo.menu).trim().toLowerCase();
        const item = [...document.querySelectorAll('header .menu-list__list-item')].find((li) => {
          const link = li.querySelector('.menu-list__link');
          return link && this.titleOf(link) === key;
        });
        const inner = item && item.querySelector('.menu-list__submenu-inner');
        if (!inner || inner.querySelector(':scope > .wf-mega-promo')) continue;
        inner.insertAdjacentHTML('beforeend', promo.html);
      }

      if (this.config.scrim && !this.scrim) {
        this.scrim = document.createElement('div');
        this.scrim.className = 'wf-menu-scrim';
        this.scrim.setAttribute('aria-hidden', 'true');
        document.body.appendChild(this.scrim);
      }
    },

    syncScrim() {
      if (!this.scrim) return;
      const open = Boolean(
        document.querySelector(
          'header .menu-list__list-item:not([slot="overflow"]) [aria-expanded="true"], header .menu-list__submenu:hover'
        )
      );
      document.documentElement.classList.toggle('wf-menu-open', open);
    },
  };

  /* ------------------------------------------------------------------ */
  /* <wf-collection-tabs>: turns the next N sections into tab panels     */
  /* ------------------------------------------------------------------ */
  class WfCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[role="tab"]')];
      this.panels = this.collectPanels();
      this.buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => this.select(i, true));
        btn.addEventListener('keydown', (e) => this.onKey(e, i));
      });
      this.select(Number(this.dataset.initial || 0), false);

      if (isEditor) {
        this._onSelect = (e) => {
          const idx = this.panels.findIndex((p) => p.id === 'shopify-section-' + e.detail.sectionId);
          if (idx > -1) this.select(idx, false);
        };
        document.addEventListener('shopify:section:select', this._onSelect);
      }
    }

    disconnectedCallback() {
      if (this._onSelect) document.removeEventListener('shopify:section:select', this._onSelect);
      (this.panels || []).forEach((p) => p.classList.remove('wf-tab-panel', 'wf-tab-panel--hidden', 'wf-tab-panel--in'));
    }

    collectPanels() {
      const host = this.closest('.shopify-section');
      const count = this.buttons.length;
      const panels = [];
      let next = host ? host.nextElementSibling : null;
      while (next && panels.length < count) {
        if (next.classList.contains('shopify-section')) panels.push(next);
        next = next.nextElementSibling;
      }
      panels.forEach((p, i) => {
        p.classList.add('wf-tab-panel');
        p.setAttribute('role', 'tabpanel');
        const btn = this.buttons[i];
        if (btn) {
          if (!p.id) p.id = 'wf-panel-' + Math.random().toString(36).slice(2);
          btn.setAttribute('aria-controls', p.id);
        }
        // Horizon product-list header lives in .section-resource-list__header
        p.querySelectorAll('.section-resource-list__header').forEach((h) => h.setAttribute('data-wf-hide-header', ''));
      });
      return panels;
    }

    select(index, animate) {
      const i = clamp(index, 0, this.buttons.length - 1);
      this.buttons.forEach((b, n) => {
        const on = n === i;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((p, n) => {
        const on = n === i;
        p.classList.toggle('wf-tab-panel--hidden', !on);
        p.classList.remove('wf-tab-panel--in');
        if (on && animate && !reduceMotion.matches) {
          void p.offsetWidth;
          p.classList.add('wf-tab-panel--in');
        }
      });
      const link = this.buttons[i] && this.buttons[i].dataset.url;
      const viewAll = this.querySelector('[data-wf-view-all]');
      if (viewAll && link) viewAll.setAttribute('href', link);
      // carousels inside a previously hidden panel need a resize to measure
      if (animate) window.dispatchEvent(new Event('resize'));
    }

    onKey(e, i) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      const n = this.buttons.length;
      const next = e.key === 'ArrowRight' ? (i + 1) % n : (i - 1 + n) % n;
      this.select(next, true);
      this.buttons[next].focus();
    }
  }
  if (!customElements.get('wf-collection-tabs')) customElements.define('wf-collection-tabs', WfCollectionTabs);

  /* ------------------------------------------------------------------ */
  /* Shared drawer (one per page)                                        */
  /* ------------------------------------------------------------------ */
  const Drawer = {
    el: null,
    lastFocus: null,

    ensure() {
      if (this.el) return this.el;
      const el = document.createElement('div');
      el.className = 'wf-drawer';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-labelledby', 'wf-drawer-title');
      el.innerHTML =
        '<div class="wf-drawer__scrim" data-wf-close></div>' +
        '<div class="wf-drawer__panel">' +
        '<div class="wf-drawer__head"><h2 class="wf-drawer__title" id="wf-drawer-title"></h2>' +
        '<button type="button" class="wf-drawer__close" data-wf-close aria-label="Close">' +
        '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5"/></svg>' +
        '</button></div>' +
        '<div class="wf-drawer__body"></div>' +
        '<div class="wf-drawer__foot"></div>' +
        '</div>';
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-wf-close]')) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && el.hasAttribute('open')) this.close();
      });
      document.body.appendChild(el);
      this.el = el;
      return el;
    },

    open({ title, body, foot }) {
      const el = this.ensure();
      this.lastFocus = document.activeElement;
      el.querySelector('.wf-drawer__title').textContent = title || '';
      const b = el.querySelector('.wf-drawer__body');
      const f = el.querySelector('.wf-drawer__foot');
      b.replaceChildren(body);
      f.replaceChildren();
      if (foot) f.appendChild(foot);
      f.hidden = !foot;
      lockScroll(true);
      requestAnimationFrame(() => {
        el.setAttribute('open', '');
        el.querySelector('.wf-drawer__close').focus({ preventScroll: true });
      });
    },

    close() {
      if (!this.el) return;
      this.el.removeAttribute('open');
      lockScroll(false);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    },
  };
  WF.drawer = Drawer;

  /* Variant resolution for a product item rendered by wf-look-item */
  const LookItem = {
    resolve(itemEl) {
      const data = itemEl._wfData || (itemEl._wfData = JSON.parse(itemEl.querySelector('script[type="application/json"]').textContent));
      const selects = [...itemEl.querySelectorAll('select[data-option-index]')];
      if (!selects.length) return data.variants.find((v) => v.available) || data.variants[0];
      const values = selects.map((s) => s.value);
      return data.variants.find((v) => v.options.every((o, i) => o === values[i])) || null;
    },

    sync(itemEl) {
      const v = this.resolve(itemEl);
      const btn = itemEl.querySelector('[data-wf-add]');
      const price = itemEl.querySelector('[data-wf-price]');
      itemEl._wfVariant = v;
      if (price && v) {
        price.innerHTML = money(v.price) + (v.compare_at_price > v.price ? ' <s>' + money(v.compare_at_price) + '</s>' : '');
      }
      if (btn) {
        const ok = Boolean(v && v.available);
        btn.disabled = !ok;
        btn.querySelector('span').textContent = ok ? btn.dataset.labelAdd : v ? btn.dataset.labelSoldout : btn.dataset.labelUnavailable;
      }
      return v;
    },

    bind(root) {
      root.querySelectorAll('.wf-look-item').forEach((item) => {
        this.sync(item);
        item.addEventListener('change', (e) => {
          if (e.target.matches('select[data-option-index]')) this.sync(item);
        });
        const btn = item.querySelector('[data-wf-add]');
        if (btn) {
          btn.addEventListener('click', async () => {
            const v = this.sync(item);
            if (!v || !v.available) return;
            await runAdd(btn, [{ id: v.id, quantity: 1 }], item);
          });
        }
      });
    },
  };

  async function runAdd(btn, items, container) {
    const label = btn.querySelector('span');
    const original = label ? label.textContent : '';
    const msg = container && container.querySelector('.wf-msg');
    if (msg) msg.remove();
    btn.setAttribute('aria-busy', 'true');
    try {
      await WF.addToCart(items, btn);
      btn.classList.add('is-added');
      if (label) label.textContent = btn.dataset.labelAdded || 'Added';
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (label) label.textContent = original;
      }, 1800);
    } catch (err) {
      const p = document.createElement('p');
      p.className = 'wf-msg';
      p.setAttribute('role', 'alert');
      p.textContent = err.message || 'Something went wrong';
      (container || btn.parentElement).appendChild(p);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }
  WF.runAdd = runAdd;

  /* ------------------------------------------------------------------ */
  /* <wf-shop-the-look>                                                  */
  /* ------------------------------------------------------------------ */
  class WfShopTheLook extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.wf-stl__track');
      this.prev = this.querySelector('[data-wf-prev]');
      this.next = this.querySelector('[data-wf-next]');
      this.videos = [...this.querySelectorAll('video')];

      this.addEventListener('click', (e) => {
        const shop = e.target.closest('[data-wf-shop]');
        if (shop) {
          e.preventDefault();
          this.openLook(shop.closest('.wf-stl__item'));
          return;
        }
        const play = e.target.closest('[data-wf-play]');
        if (play) this.togglePlay(play.closest('.wf-stl__item'));
      });

      if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
      if (this.next) this.next.addEventListener('click', () => this.page(1));
      if (this.track) {
        this.track.addEventListener('scroll', debounce(() => this.updateNav(), 50), { passive: true });
        this.updateNav();
      }

      // Only play videos that are on screen (saves battery / data)
      if ('IntersectionObserver' in window && this.videos.length) {
        this.io = new IntersectionObserver(
          (entries) => {
            for (const en of entries) {
              const item = en.target.closest('.wf-stl__item');
              if (en.isIntersecting && !item.classList.contains('is-paused') && !reduceMotion.matches) {
                en.target.play().catch(() => {});
              } else {
                en.target.pause();
              }
            }
          },
          { threshold: 0.35 }
        );
        this.videos.forEach((v) => this.io.observe(v));
      }
      if (reduceMotion.matches) this.querySelectorAll('.wf-stl__item').forEach((i) => i.classList.add('is-paused'));
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }

    page(dir) {
      if (!this.track) return;
      const item = this.track.querySelector('.wf-stl__item');
      const step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 16) : 300;
      this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    updateNav() {
      if (!this.track || !this.prev) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max;
      const nav = this.querySelector('.wf-stl__nav');
      if (nav) nav.hidden = max <= 0;
    }

    togglePlay(item) {
      const v = item && item.querySelector('video');
      if (!v) return;
      if (v.paused) {
        item.classList.remove('is-paused');
        v.play().catch(() => {});
      } else {
        item.classList.add('is-paused');
        v.pause();
      }
    }

    openLook(item) {
      if (!item) return;
      const tpl = item.querySelector('template[data-wf-look]');
      if (!tpl) return;
      const body = document.createElement('div');
      body.style.display = 'contents';
      body.appendChild(tpl.content.cloneNode(true));
      body.querySelectorAll('.wf-look-item').forEach((el, i) => el.style.setProperty('--wf-i', i));

      let foot = null;
      const count = body.querySelectorAll('.wf-look-item').length;
      if (count > 1) {
        foot = document.createElement('div');
        foot.innerHTML =
          '<button type="button" class="wf-btn" data-label-added="' +
          (this.dataset.labelAdded || 'Added') +
          '"><span>' +
          (this.dataset.labelAddAll || 'Add all to cart') +
          '</span></button>';
        const allBtn = foot.querySelector('button');
        allBtn.addEventListener('click', () => {
          const lines = [...Drawer.el.querySelectorAll('.wf-look-item')]
            .map((el) => el._wfVariant)
            .filter((v) => v && v.available)
            .map((v) => ({ id: v.id, quantity: 1 }));
          if (lines.length) runAdd(allBtn, lines, foot);
        });
      }

      Drawer.open({ title: this.dataset.drawerTitle || '', body, foot });
      LookItem.bind(Drawer.el.querySelector('.wf-drawer__body'));
    }
  }
  if (!customElements.get('wf-shop-the-look')) customElements.define('wf-shop-the-look', WfShopTheLook);

  /* ------------------------------------------------------------------ */
  /* <wf-parallax-split>: sticky split with floating cards               */
  /* ------------------------------------------------------------------ */
  class WfParallaxSplit extends HTMLElement {
    connectedCallback() {
      this.sticky = this.querySelector('.wf-px__sticky');
      this.panel = this.querySelector('.wf-px__panel');
      this.bg = this.querySelector('.wf-px__media > img, .wf-px__media > svg');
      this.hint = this.querySelector('.wf-px__hint');
      this.cards = [...this.querySelectorAll('.wf-px__card')].map((el) => ({
        el,
        start: Number(el.dataset.start || 0) / 100,
        end: Number(el.dataset.end || 100) / 100,
        rot: Number(el.dataset.rot || 0),
        spin: Number(el.dataset.spin || 0),
      }));
      this.ticking = false;
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      this.mq = window.matchMedia('(max-width: 749px)');
      this.onResize = () => {
        this.bindScroller();
        this.setTop();
        this.onScroll();
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      this.bindScroller();
      this.setTop();
      this.update();
    }

    bindScroller() {
      const next = scrollParent(this);
      if (next === this.scroller) return;
      if (this.scroller) this.scroller.removeEventListener('scroll', this.onScroll);
      this.scroller = next;
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      if (this.scroller) this.scroller.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    setTop() {
      // keep the sticky stage below a sticky Horizon header
      const header = document.querySelector('#header-component[sticky]');
      const h = header ? header.getBoundingClientRect().height : 0;
      this.style.setProperty('--wf-px-top', Math.round(h) + 'px');
    }

    update() {
      if (reduceMotion.matches) return;
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight;
      const mobile = this.mq.matches;
      // progress 0 → 1 while the sticky stage is pinned (desktop),
      // or while the section crosses the viewport (mobile)
      const top = parseFloat(this.style.getPropertyValue('--wf-px-top')) || 0;
      const total = mobile ? rect.height + vh : rect.height - this.sticky.offsetHeight;
      const p = clamp(mobile ? (vh - rect.top) / total : (top - rect.top) / Math.max(total, 1));

      if (this.bg) this.bg.style.setProperty('--wf-px-bg-shift', ((p - 0.5) * -60).toFixed(1) + 'px');
      if (this.hint) this.hint.style.opacity = String(clamp(1 - p * 6) * 0.7);

      const panelH = this.panel.offsetHeight;
      // cards scale with the panel so they keep their proportion on wide screens
      const scale = clamp(this.panel.offsetWidth / 720, 0.75, 1.8);
      if (Math.abs(scale - (this._scale || 0)) > 0.01) {
        this._scale = scale;
        this.style.setProperty('--wf-px-scale', scale.toFixed(3));
      }
      for (const c of this.cards) {
        const span = Math.max(c.end - c.start, 0.01);
        const t = clamp((p - c.start) / span);
        const cardH = c.el.offsetHeight || 200;
        const y = panelH + 20 - t * (panelH + cardH + 40);
        c.el.style.setProperty('--wf-y', y.toFixed(1) + 'px');
        c.el.style.setProperty('--wf-rot', (c.rot + c.spin * t).toFixed(2) + 'deg');
      }
    }
  }
  if (!customElements.get('wf-parallax-split')) customElements.define('wf-parallax-split', WfParallaxSplit);

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */
  const boot = () => {
    Menu.init();
    WF.reveal();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    WF.reveal(e.target);
    if (e.target.querySelector && e.target.querySelector('#wf-effects-config')) Menu.init();
  });
}
